import React, { useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Descriptions,
    Drawer,
    Empty,
    Input,
    Modal,
    Pagination,
    Progress,
    Segmented,
    Select,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AppstoreOutlined, BarsOutlined, InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { WEBSITE_MEDIA_LIST } from '../graphql/queries';
import { BATCH_DELETE_WEBSITE_MEDIA, DELETE_WEBSITE_MEDIA } from '../graphql/mutations';
import type { WebsiteMediaBatchResult, WebsiteMediaItem, WebsiteMediaList, WebsiteMediaType } from '../graphql/types';
import { copyText } from '../utils/actions';
import { resolveWebsiteContentError } from '../utils/errorMessage';
import { formatFileSize, formatLocalDateTime } from '../utils/format';
import { MEDIA_ACCEPT } from './mediaFormat';
import { describeUploadError, uploadWebsiteMedia } from './uploadMedia';
import { askDuplicateMedia } from './confirmDuplicate';
import MediaThumb from './MediaThumb';

const PAGE_SIZE = 40;
const MAX_KEYWORD_LENGTH = 100;
const MEDIA_TYPES: WebsiteMediaType[] = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const ALL = 'ALL';
const VIEW_STORAGE_KEY = 'swiftx_website_media_view';

type ViewMode = 'grid' | 'list';

interface QueueItem {
    key: string;
    file: File;
    progress: number;
    error: string | null;
}

const parseType = (value: string | null): WebsiteMediaType | null =>
    (MEDIA_TYPES.includes(value as WebsiteMediaType) ? (value as WebsiteMediaType) : null);

const describeSize = (item: WebsiteMediaItem): string => {
    const parts = [formatFileSize(item.sizeBytes)];
    if (item.width && item.height) parts.push(`${item.width}×${item.height}`);
    if (item.durationSeconds) parts.push(`${Math.floor(item.durationSeconds / 60)}:${String(item.durationSeconds % 60).padStart(2, '0')}`);
    return parts.join(' · ');
};

/**
 * 媒体库（T-A15）：网格 / 列表切换，按类型筛选、文件名搜索、只看未被引用；拖拽多选上传（进度、失败重试）；
 * 详情抽屉；删除前检查引用（被引用时不可删）；多选批量删除（被引用的跳过）；上传同名同大小文件时询问是否跳过。
 * 筛选与页码写进地址，按上传时间倒序，每页 40 个。
 */
const WebsiteMediaLibraryPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const mediaType = parseType(searchParams.get('mediaType'));
    const keyword = searchParams.get('keyword') ?? '';
    const unreferencedOnly = searchParams.get('unreferencedOnly') === 'true';
    const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '', 10) || 1);
    const [keywordInput, setKeywordInput] = useState(keyword);
    const [view, setView] = useState<ViewMode>(() =>
        (window.localStorage.getItem(VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid'));
    const [detail, setDetail] = useState<WebsiteMediaItem | null>(null);
    const [queue, setQueue] = useState<QueueItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const canDelete = hasResource([ResourceCode.websiteContent.MEDIA_MANAGE]);

    useEffect(() => setKeywordInput(keyword), [keyword]);

    const { data, previousData, loading, error, refetch } = useQuery<{ websiteMediaList: WebsiteMediaList }>(
        WEBSITE_MEDIA_LIST,
        {
            variables: { mediaType, keyword: keyword || null, unreferencedOnly: unreferencedOnly || null, page, pageSize: PAGE_SIZE },
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
        },
    );
    const list = (data ?? previousData)?.websiteMediaList;
    const [deleteMedia, { loading: deleting }] = useMutation(DELETE_WEBSITE_MEDIA);
    const [batchDeleteMedia, { loading: batchDeleting }] = useMutation<{ batchDeleteWebsiteMedia: WebsiteMediaBatchResult }>(
        BATCH_DELETE_WEBSITE_MEDIA,
    );

    // 翻页、换筛选后只保留当前页仍在的选择
    useEffect(() => {
        const visible = new Set((list?.items ?? []).map((item) => item.id));
        setSelectedIds((ids) => ids.filter((id) => visible.has(id)));
    }, [list]);

    useEffect(() => {
        if (!error) return;
        const text = resolveWebsiteContentError(error, t);
        if (text) message.error(text);
    }, [error, t]);

    const updateParams = (patch: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
        });
        if (!('page' in patch)) next.delete('page');
        setSearchParams(next);
    };

    const changeView = (next: ViewMode) => {
        setView(next);
        window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    };

    const startUpload = (item: QueueItem) => {
        const update = (patch: Partial<QueueItem>) =>
            setQueue((items) => items.map((current) => (current.key === item.key ? { ...current, ...patch } : current)));
        update({ progress: 0, error: null });
        uploadWebsiteMedia(item.file, {
            onProgress: (progress) => update({ progress }),
            confirmDuplicate: askDuplicateMedia(t, 'skip'),
            onWarning: (warning) => message.warning(`${item.file.name}：${t(`websiteContent.upload.warning.${warning}`)}`, 8),
        }).then(() => {
            setQueue((items) => items.filter((current) => current.key !== item.key));
            refetch();
        }).catch((e) => {
            update({ error: describeUploadError(e, t) ?? String(t('websiteContent.upload.transferFailed')) });
        });
    };

    const enqueue = (files: File[]) => {
        const items = files.map((file) => ({
            key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
            file,
            progress: 0,
            error: null,
        }));
        setQueue((current) => [...current, ...items]);
        items.forEach(startUpload);
    };

    const handleDelete = (item: WebsiteMediaItem) => {
        Modal.confirm({
            title: t('websiteContent.media.confirmDelete', { name: item.fileName }),
            content: t('websiteContent.media.confirmDeleteHint'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteMedia({ variables: { id: item.id } });
                    message.success(t('websiteContent.media.deleted'));
                    setDetail(null);
                    refetch();
                } catch (e) {
                    const text = resolveWebsiteContentError(e, t);
                    if (text) message.error(text);
                }
            },
        });
    };

    const toggleSelected = (id: string, checked: boolean) =>
        setSelectedIds((ids) => (checked ? [...ids, id] : ids.filter((current) => current !== id)));

    const handleBatchDelete = () => {
        Modal.confirm({
            title: t('websiteContent.media.batchDeleteConfirm', { count: selectedIds.length }),
            content: t('websiteContent.media.batchDeleteHint'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const { data: result } = await batchDeleteMedia({ variables: { ids: selectedIds } });
                    const deleted = result?.batchDeleteWebsiteMedia.succeededIds.length ?? 0;
                    const skipped = result?.batchDeleteWebsiteMedia.skipped.length ?? 0;
                    if (skipped > 0) message.warning(t('websiteContent.media.batchDeletePartial', { deleted, skipped }));
                    else message.success(t('websiteContent.media.batchDeleted', { count: deleted }));
                    setSelectedIds([]);
                    refetch();
                } catch (e) {
                    const text = resolveWebsiteContentError(e, t);
                    if (text) message.error(text);
                }
            },
        });
    };

    const copyUrl = (item: WebsiteMediaItem) => {
        copyText(item.url)
            .then(() => message.success(t('websiteContent.media.urlCopied')))
            .catch(() => message.error(t('websiteContent.list.copyFailed')));
    };

    const referencedTag = (item: WebsiteMediaItem) => (item.referencedCount > 0
        ? <Tag color="blue">{t('websiteContent.media.referenced', { count: item.referencedCount })}</Tag>
        : <Tag>{t('websiteContent.media.unreferenced')}</Tag>);

    const columns: ColumnsType<WebsiteMediaItem> = [
        {
            title: t('websiteContent.media.columns.preview'),
            key: 'preview',
            width: 112,
            render: (_, item) => <div className="w-24 aspect-video rounded overflow-hidden"><MediaThumb item={item} /></div>,
        },
        {
            title: t('websiteContent.media.columns.fileName'),
            key: 'fileName',
            render: (_, item) => (
                <Typography.Link onClick={() => setDetail(item)} className="break-all">{item.fileName}</Typography.Link>
            ),
        },
        {
            title: t('websiteContent.media.columns.type'),
            key: 'type',
            width: 90,
            render: (_, item) => t(`websiteContent.media.type.${item.mediaType}`),
        },
        { title: t('websiteContent.media.columns.size'), key: 'size', width: 200, render: (_, item) => describeSize(item) },
        { title: t('websiteContent.media.columns.references'), key: 'references', width: 120, render: (_, item) => referencedTag(item) },
        {
            title: t('websiteContent.media.columns.uploadedAt'),
            key: 'createdAt',
            width: 160,
            render: (_, item) => formatLocalDateTime(item.createdAt),
        },
    ];

    return (
        <div className="flex flex-col gap-4 min-h-0">
            <Typography.Title level={4} className="!mb-0">{t('websiteContent.media.title')}</Typography.Title>

            <Upload.Dragger
                multiple
                showUploadList={false}
                accept={[MEDIA_ACCEPT.IMAGE, MEDIA_ACCEPT.VIDEO, MEDIA_ACCEPT.DOCUMENT].join(',')}
                beforeUpload={(file, fileList) => {
                    // antd 对每个文件各调用一次；只在最后一个时整批入队
                    if (file === fileList[fileList.length - 1]) enqueue(fileList as unknown as File[]);
                    return Upload.LIST_IGNORE;
                }}
            >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">{t('websiteContent.media.uploadText')}</p>
                <p className="ant-upload-hint">{t('websiteContent.media.uploadHint')}</p>
            </Upload.Dragger>

            {queue.length > 0 && (
                <div className="flex flex-col gap-2">
                    {queue.map((item) => (
                        <div key={item.key} className="flex items-center gap-3 text-sm">
                            <span className="w-64 truncate">{item.file.name}</span>
                            {item.error
                                ? (
                                    <>
                                        <span className="flex-1 text-red-500 truncate">{item.error}</span>
                                        <Button size="small" icon={<ReloadOutlined />} onClick={() => startUpload(item)}>
                                            {t('websiteContent.media.retry')}
                                        </Button>
                                        <Button size="small" type="text"
                                            onClick={() => setQueue((items) => items.filter((current) => current.key !== item.key))}>
                                            {t('websiteContent.editor.remove')}
                                        </Button>
                                    </>
                                )
                                : <Progress className="flex-1 !mb-0" percent={item.progress} size="small" />}
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <Input.Search
                    className="!w-80"
                    allowClear
                    maxLength={MAX_KEYWORD_LENGTH}
                    value={keywordInput}
                    placeholder={String(t('websiteContent.media.searchPlaceholder'))}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onSearch={(value) => updateParams({ keyword: value.trim() || null })}
                />
                <Select
                    className="w-36"
                    value={mediaType ?? ALL}
                    onChange={(value) => updateParams({ mediaType: value === ALL ? null : value })}
                    options={[
                        { value: ALL, label: t('websiteContent.media.allTypes') },
                        ...MEDIA_TYPES.map((type) => ({ value: type, label: t(`websiteContent.media.type.${type}`) })),
                    ]}
                />
                <Checkbox
                    checked={unreferencedOnly}
                    onChange={(e) => updateParams({ unreferencedOnly: e.target.checked ? 'true' : null })}
                >
                    {t('websiteContent.media.unreferencedOnly')}
                </Checkbox>
                <div className="flex-1" />
                {canDelete && selectedIds.length > 0 && (
                    <>
                        <span className="text-sm text-gray-500">{t('websiteContent.media.selectedCount', { count: selectedIds.length })}</span>
                        <Button size="small" type="link" className="!px-0" onClick={() => setSelectedIds([])}>
                            {t('websiteContent.media.clearSelection')}
                        </Button>
                        <Button danger loading={batchDeleting} onClick={handleBatchDelete}>
                            {t('websiteContent.media.batchDelete')}
                        </Button>
                    </>
                )}
                <Segmented
                    value={view}
                    onChange={(value) => changeView(value as ViewMode)}
                    options={[
                        { value: 'grid', icon: <AppstoreOutlined />, title: String(t('websiteContent.media.gridView')) },
                        { value: 'list', icon: <BarsOutlined />, title: String(t('websiteContent.media.listView')) },
                    ]}
                />
            </div>

            {view === 'grid'
                ? (
                    <Spin spinning={loading}>
                        {list && list.items.length === 0
                            ? <Empty description={t('websiteContent.media.empty')} />
                            : (
                                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                                    {list?.items.map((item) => (
                                        <div key={item.id} className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setDetail(item)}
                                                className="w-full text-left rounded border border-gray-200 overflow-hidden hover:border-purple-500 bg-white"
                                            >
                                                <div className="aspect-video"><MediaThumb item={item} /></div>
                                                <div className="px-2 pt-1 text-xs truncate">
                                                    <Tooltip title={item.fileName} mouseEnterDelay={0.5}>{item.fileName}</Tooltip>
                                                </div>
                                                <div className="px-2 pb-2 flex items-center justify-between text-xs text-gray-400">
                                                    <span>{formatFileSize(item.sizeBytes)}</span>
                                                    {referencedTag(item)}
                                                </div>
                                            </button>
                                            {canDelete && (
                                                <Checkbox
                                                    className="!absolute top-1.5 left-2"
                                                    checked={selectedIds.includes(item.id)}
                                                    onChange={(e) => toggleSelected(item.id, e.target.checked)}
                                                    aria-label={item.fileName}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                    </Spin>
                )
                : (
                    <Table<WebsiteMediaItem>
                        rowKey="id"
                        columns={columns}
                        dataSource={list?.items ?? []}
                        loading={loading}
                        pagination={false}
                        locale={{ emptyText: t('websiteContent.media.empty') }}
                        rowSelection={canDelete
                            ? { selectedRowKeys: selectedIds, onChange: (keys) => setSelectedIds(keys.map(String)) }
                            : undefined}
                    />
                )}

            <div className="flex justify-end">
                <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={list?.total ?? 0}
                    showSizeChanger={false}
                    showTotal={(total) => t('websiteContent.list.total', { total })}
                    onChange={(next) => updateParams({ page: next > 1 ? String(next) : null })}
                />
            </div>

            <Drawer
                open={Boolean(detail)}
                width={480}
                title={detail?.fileName}
                onClose={() => setDetail(null)}
                extra={detail && canDelete && (
                    <Tooltip title={detail.referencedCount > 0 ? t('websiteContent.media.inUseHint', { count: detail.referencedCount }) : undefined}>
                        <Button danger loading={deleting} disabled={detail.referencedCount > 0} onClick={() => handleDelete(detail)}>
                            {t('websiteContent.actions.delete')}
                        </Button>
                    </Tooltip>
                )}
            >
                {detail && (
                    <div className="flex flex-col gap-4">
                        <div className="rounded overflow-hidden border border-gray-200">
                            {detail.mediaType === 'VIDEO'
                                ? <video src={detail.url} controls preload="metadata" className="w-full" />
                                : detail.mediaType === 'IMAGE'
                                    ? <img src={detail.url} alt={detail.fileName} className="w-full" />
                                    : <div className="aspect-video"><MediaThumb item={detail} /></div>}
                        </div>
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label={t('websiteContent.media.columns.type')}>
                                {t(`websiteContent.media.type.${detail.mediaType}`)} · {detail.mimeType}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('websiteContent.media.columns.size')}>{describeSize(detail)}</Descriptions.Item>
                            <Descriptions.Item label={t('websiteContent.media.columns.references')}>{referencedTag(detail)}</Descriptions.Item>
                            <Descriptions.Item label={t('websiteContent.media.columns.uploadedAt')}>
                                {formatLocalDateTime(detail.createdAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('websiteContent.media.url')}>
                                <div className="flex items-center gap-2">
                                    <a href={detail.url} target="_blank" rel="noopener noreferrer" className="break-all text-xs">{detail.url}</a>
                                    <Button size="small" onClick={() => copyUrl(detail)}>{t('websiteContent.media.copyUrl')}</Button>
                                </div>
                            </Descriptions.Item>
                        </Descriptions>
                        {detail.referencedCount > 0 && (
                            <Typography.Text type="secondary">{t('websiteContent.media.inUseHint', { count: detail.referencedCount })}</Typography.Text>
                        )}
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default WebsiteMediaLibraryPage;
